import { Label } from 'react-aria-components';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Control, Controller, FieldPath, FieldValues, FieldError } from 'react-hook-form';

import { ja } from 'date-fns/locale/ja';

type Props<T extends FieldValues> = {
  name: FieldPath<T>;
  label?: string;
  control: Control<T>;
  required?: boolean;
  errors?: FieldError | undefined;
};

registerLocale('ja', ja);

const DateField = <T extends FieldValues>(props: Props<T>) => {
  return (
    <div className="relative w-full">
      {props.label && (
        <div className="flex flex-row items-center">
          <Label className="mb-2 text-lg font-bold">{props.label}</Label>
          {props.required && (
            <span className="mb-2 ml-1 text-base text-red-800 md:text-sm">※必須</span>
          )}
        </div>
      )}
      <Controller
        name={props.name}
        control={props.control}
        rules={{ required: props.required }}
        render={({ field: { onChange, value } }) => {
          return (
            <DatePicker
              id="date"
              selected={getValidDate(value)}
              onChange={(date) => {
                if (date instanceof Date) {
                  onChange(date);
                } else {
                  onChange(null);
                }
              }}
              dateFormat="yyyy/MM/dd"
              locale="ja"
              minDate={new Date('1900-01-01')}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              placeholderText="選択してください"
              className="mb-2 block h-10 w-full rounded-md border border-[#1A1A1A] px-3 py-2 focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm md:h-14"
              wrapperClassName="w-full"
              popperClassName="react-datepicker-popper"
              popperPlacement="bottom-start"
            />
          );
        }}
      />
      {props.errors && (
        <span className="mb-2 w-fit text-sm text-red-800">{props.errors.message}</span>
      )}
    </div>
  );
};

const getValidDate = (value: string | Date | null): Date | null => {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);

  if (date.getFullYear() < 1900) {
    return null;
  }

  return !isNaN(date.getTime()) ? date : null;
};

export default DateField;
